/**
 * Hero — première impression, slogan + CTAs + 4 stats animées.
 */
import { Link } from "react-router-dom";
import { colors, fonts, goldGradient } from "../../styles/tokens";
import Button from "../Button";
import useCountUp from "../../hooks/useCountUp";

const STATS = [
  { value: 400000, suffix: "", label: "Églises francophones dans le monde" },
  { value: 88,     suffix: "",  label: "Pays concernés" },
  { value: 0,      suffix: "€",  label: "Pour commencer" },
  { value: 5,      suffix: " min", label: "Pour être opérationnel" },
];

export default function Hero() {
  return (
    <section
      style={{
        position: "relative",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        padding: "clamp(96px, 14vw, 120px) clamp(16px, 4vw, 24px) clamp(64px, 10vw, 80px)",
        background: colors.bg,
        overflow: "hidden",
        width: "100%",
        maxWidth: "100vw",
      }}
    >
      {/* Grille subtile en fond */}
      <div style={{
        position: "absolute", inset: 0,
        backgroundImage: `linear-gradient(${colors.border} 1px, transparent 1px), linear-gradient(90deg, ${colors.border} 1px, transparent 1px)`,
        backgroundSize: "64px 64px",
        opacity: 0.15,
        pointerEvents: "none",
      }} />

      {/* Orbes de lumière */}
      <div style={{
        position: "absolute",
        top: "10%", left: "-10%",
        width: "600px", height: "600px",
        background: "radial-gradient(circle, rgba(228,167,44,0.18) 0%, transparent 60%)",
        pointerEvents: "none",
      }} />
      <div style={{
        position: "absolute",
        bottom: "0%", right: "-15%",
        width: "700px", height: "700px",
        background: "radial-gradient(circle, rgba(245,197,90,0.12) 0%, transparent 60%)",
        pointerEvents: "none",
      }} />

      <div style={{
        position: "relative",
        maxWidth: 1100,
        margin: "0 auto",
        textAlign: "center",
        fontFamily: fonts.body,
      }}>
        <h1 style={{
          fontFamily: fonts.heading,
          fontSize: "clamp(2rem, 8vw, 4.5rem)",
          fontWeight: 700,
          lineHeight: 1.1,
          color: colors.text,
          margin: 0,
          letterSpacing: "-0.02em",
          width: "100%",
          maxWidth: "100%",
          padding: "0 1.5rem",
          wordBreak: "break-word",
          overflowWrap: "break-word",
        }}>
          Votre ministère mérite{" "}
          <span style={{
            background: goldGradient,
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            color: "transparent",
            display: "inline-block",
          }}>
            les meilleurs outils.
          </span>
        </h1>

        <p style={{
          fontSize: "clamp(15px, 1.4vw, 18px)",
          color: colors.textMuted,
          maxWidth: 720,
          margin: "24px auto 36px",
          lineHeight: 1.6,
        }}>
          Finis les cahiers, les Excel désorganisés et les groupes WhatsApp.
          Une seule application — simple, belle, 100&nbsp;% en français — pour gérer
          votre communauté de 10 à 10&nbsp;000 membres.
        </p>

        <div className="hero-ctas" style={{
          display: "flex",
          gap: "12px",
          justifyContent: "center",
          flexWrap: "wrap",
          marginBottom: "64px",
          width: "100%",
        }}>
          <Link to="/inscription" style={{ textDecoration: "none", display: "block", width: "100%", maxWidth: "320px" }} className="hero-cta-link">
            <Button size="lg" fullWidth>Commencer gratuitement →</Button>
          </Link>
          <a
            href="#fonctionnalites"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById("fonctionnalites")?.scrollIntoView({ behavior: "smooth" });
            }}
            style={{ textDecoration: "none", display: "block", width: "100%", maxWidth: "320px" }}
            className="hero-cta-link"
          >
            <Button size="lg" variant="ghost" fullWidth style={{ borderColor: colors.gold, color: colors.gold }}>
              Voir les fonctionnalités
            </Button>
          </a>
        </div>

        {/* Stats */}
        <div className="hero-stats" style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: "24px",
          maxWidth: 900,
          margin: "0 auto",
          paddingTop: "32px",
          borderTop: `1px solid ${colors.border}`,
        }}>
          {STATS.map((s) => (
            <StatItem key={s.label} value={s.value} suffix={s.suffix} label={s.label} />
          ))}
        </div>
      </div>

      {/* Breakpoints inline pour le Hero */}
      <style>{`
        @media (max-width: 768px) {
          /* CTAs en colonne pleine largeur (max 320px) sur mobile */
          .hero-ctas {
            flex-direction: column !important;
            align-items: center;
            gap: 10px !important;
          }
          /* Stats en 2 × 2 sur mobile */
          .hero-stats {
            grid-template-columns: 1fr 1fr !important;
            gap: 18px !important;
          }
        }
      `}</style>
    </section>
  );
}

function StatItem({ value, suffix, label }) {
  const [ref, display] = useCountUp(value, { duration: 1800 });
  return (
    <div ref={ref} style={{ textAlign: "center" }}>
      <div style={{
        fontFamily: fonts.heading,
        fontSize: "clamp(28px, 3vw, 40px)",
        fontWeight: 700,
        color: colors.gold,
        lineHeight: 1.1,
      }}>
        {display}{suffix}
      </div>
      <div style={{
        fontSize: "13px",
        color: colors.textMuted,
        marginTop: "6px",
        lineHeight: 1.4,
      }}>
        {label}
      </div>
    </div>
  );
}
